import React from 'react';

import { getLayout, IPrismicLayout } from 'providers/prismic/layout';
import { localeToLang } from 'providers/prismic/utils/localeToLang';
import { getArticleBySlug, IArticle } from 'providers/prismic/articles';
import { Layout } from 'containers/layout/Layout';
import { Header } from 'components/header/Header';
import { ArticleHero } from 'components/hero/ArticleHero';
import { Slices } from 'containers/slices/Slices';
import { createSlug } from 'providers/prismic/utils/createSlug';
import { config } from 'utils/config';

type Props = {
  preview?: boolean;
  layout: IPrismicLayout;
  article: IArticle;
  url: string | null;
};

export default function ArticlePage({ preview, layout, article, url }: Props) {
  const [backLinkHistory, setBackLinkHistory] = React.useState(false);

  React.useEffect(() => {
    // if referrer is same site, use history.back() for back button
    const origin = !window.location.origin
      ? window.location.protocol +
        '//' +
        window.location.hostname +
        (window.location.port ? ':' + window.location.port : '')
      : window.location.origin;

    const isSameSite = window.document.referrer.includes(origin) ?? false;

    setBackLinkHistory(isSameSite);
  }, []);

  const handleBackClick = (event) => {
    if (backLinkHistory) {
      event.preventDefault();
      window.history.back();
    }
  };

  const extract = article.extract && `${article.extract?.slice(0, 147)}...`;

  return (
    <Layout
      layout={layout}
      seo={{
        title: article.seo.title || article.title,
        description: article.seo.description || article.summary || extract,
        image: article.seo.image || article.image,
        canonicalUrl: url,
      }}
      header={<Header showNavigation={layout.navigationMain.length > 0} />}
      hasFooter
      isPadded
      preview={preview}
    >
      <ArticleHero
        title={article.title}
        tagline={article.tagline}
        date={article.date}
        image={article.showImage && article.image}
        caption={article.caption}
        back="/"
        backLabel="Back to Newsroom"
        backOnClick={handleBackClick}
      />
      <Slices slices={article.slices} isArticle />
    </Layout>
  );
}

export async function getStaticProps({ params, preview = false, previewData, locale }) {
  const lang = localeToLang(locale);

  const layout = await getLayout({ previewData, lang });
  const article = await getArticleBySlug(params.article, {
    previewData,
    lang,
    redirects: layout?.redirects,
  });

  const url = createSlug({
    includeSiteUrl: true,
    lang: lang,
    prefix: 'newsroom',
    uid: article?.uid,
    layouts: [layout],
  });

  return {
    notFound: !article,
    props: {
      preview,
      layout,
      article,
      url,
    },
    revalidate: config.revalidate,
  };
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}
